/**
 * Product add-on reconciler (Greeting Card, Bottle Engraving), UX layer.
 *
 * Add-on lines are linked to their parent (bottle) line by the hidden `_addon_group`
 * property; add-on lines also carry `_addon_mode` ('match' = quantity follows the
 * parent, 'single' = always 1). After any cart change this keeps every group
 * consistent:
 *  - parent gone: every add-on line in the group is removed
 *  - 'match' add-on: quantity set to the parent quantity
 *  - 'single' add-on: quantity set to 1
 *  - add-on line with no group at all: removed (malformed)
 *
 * It never ADDS lines, so an add-on the shopper removed with the trash icon stays
 * removed. Lines without `_addon_mode` (bottles, free gifts, legacy Giftship lines)
 * are never touched. Mirrors assets/free-gift.js: raw /cart/*.js calls, sequential
 * mutations by line key, one UI refresh at the end. See docs/product-addons-plan.md.
 */
(function () {
  var GROUP_PROPERTY = '_addon_group';
  var MODE_PROPERTY = '_addon_mode';

  var routes = (window.CartIncentives && window.CartIncentives.routes) || {};
  var cartRoute = routes.cart || '/cart';
  var changeRoute = routes.cartChange || '/cart/change';

  function getCart() {
    // `cache: 'no-store'`: a cached /cart.js snapshot from before the last change
    // would make every decision below wrong.
    return fetch(cartRoute + '.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (r) {
      return r.json();
    });
  }

  function prop(item, name) {
    return item.properties && item.properties[name] ? String(item.properties[name]) : '';
  }

  function isAddon(item) {
    return Boolean(prop(item, MODE_PROPERTY));
  }

  // Addresses the line by its 1-based position, resolved from a fresh /cart.js at call
  // time. Shopify's /cart/change.js rejects a line KEY with "no valid id or line
  // parameter" whenever another line holds the same variant (a plain bottle next to
  // the same bottle with add-ons), so keys cannot be relied on. Resolving the position
  // per call also keeps sequential changes correct after a removal shifts the lines.
  function setLineQuantity(line, quantity) {
    return getCart().then(function (cart) {
      var index = cart.items.findIndex(function (item) { return item.key === line.key; });
      if (index === -1) return null; // already gone

      return fetch(changeRoute + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: index + 1, quantity: quantity })
      }).then(function (r) {
        // A rejected change (typically 422: the add-on product tracks inventory and
        // has fewer units than the bottle quantity) would otherwise fail silently.
        if (!r.ok) {
          return r.json().catch(function () { return {}; }).then(function (body) {
            console.log('[cart-addons] change rejected for line', index + 1, 'to', quantity, '(status ' + r.status + ')', body); // eslint-disable-line no-console
            return r;
          });
        }
        return r;
      });
    });
  }

  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    var cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') cartItems.refresh();
    // Raw /cart/*.js calls emit none of the theme's cart events, so tell the other
    // reconciler (free-gift.js) the cart moved; it decides on a fresh snapshot.
    document.dispatchEvent(new CustomEvent('on:cart:reconciled', { detail: { source: 'cart-addons' } }));
  }

  // Returns the list of { line, quantity } changes needed for one cart snapshot.
  function planChanges(cart) {
    var parents = {};
    var changes = [];

    cart.items.forEach(function (item) {
      var group = prop(item, GROUP_PROPERTY);
      if (group && !isAddon(item)) parents[group] = item;
    });

    cart.items.forEach(function (item) {
      if (!isAddon(item)) return;

      var group = prop(item, GROUP_PROPERTY);
      var parent = group ? parents[group] : null;

      if (!parent) {
        changes.push({ line: item, quantity: 0 });
        return;
      }

      var wanted = prop(item, MODE_PROPERTY) === 'match' ? parent.quantity : 1;
      if (item.quantity !== wanted) {
        changes.push({ line: item, quantity: wanted });
      }
    });

    return changes;
  }

  // Runs through the shared queue (see snippets/cart-incentive-config.liquid) so this
  // never overlaps free-gift.js: both address lines by position, and two reconcilers
  // acting on their own snapshots at once would shift each other's targets. A run
  // requested while one is already waiting is folded into it.
  var enqueue = (window.CartIncentives && window.CartIncentives.enqueue) || function (task) { return task(); };
  var queued = false;

  function reconcile() {
    if (queued) return Promise.resolve();
    queued = true;
    return enqueue(function () {
      queued = false;
      return runReconcile();
    });
  }

  function runReconcile() {
    var changed = false;

    return getCart()
      .then(function (cart) {
        var changes = planChanges(cart);
        // Sequential: cart mutations share a lock, and each change re-resolves its
        // line's position, so every decision can be made from the one snapshot.
        return changes.reduce(function (chain, change) {
          return chain.then(function () {
            changed = true;
            return setLineQuantity(change.line, change.quantity);
          });
        }, Promise.resolve());
      })
      .catch(function (error) {
        console.log('[cart-addons] reconcile failed', error); // eslint-disable-line no-console
      })
      .then(function () {
        // Refreshing announces on:cart:reconciled, which re-runs both reconcilers on a
        // fresh snapshot. That settles: a run that changes nothing announces nothing.
        if (changed) refreshCartUI();
      });
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  var debouncedReconcile = debounce(reconcile, 400);

  // on:cart:add fires for a newly added variant, on:line-item:change for quantity
  // edits, removals, and re-adds of a variant already in the cart.
  document.addEventListener('on:cart:add', debouncedReconcile);
  document.addEventListener('on:line-item:change', debouncedReconcile);
  // The free-gift reconciler changed the cart; re-check the groups on the result.
  document.addEventListener('on:cart:reconciled', debouncedReconcile);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
